/**
 * Theme Registry
 *
 * Central registry for all application themes.
 * Provides helper functions for theme discovery, filtering, and management.
 *
 * To add a new theme:
 * 1. Create a new theme file (e.g., educator-ocean-light.ts)
 * 2. Import and register it in the THEME_REGISTRY below
 * 3. The theme will automatically be available in the appropriate context
 */

import { Theme, ThemeCategory, ThemeMode } from './theme-types';
import { studentGlassLight } from './student-glass-light';
import { educatorGlassLight } from './educator-glass-light';

/**
 * Central theme registry
 * All themes must be registered here to be available in the application
 */
const THEME_REGISTRY: Record<string, Theme> = {
  // Student themes
  'student-glass-light': studentGlassLight,

  // Educator themes
  'educator-glass-light': educatorGlassLight,
};

/**
 * Default theme IDs per category
 */
const DEFAULT_THEMES: Record<ThemeCategory, string> = {
  student: 'student-glass-light',
  educator: 'educator-glass-light',
};

/**
 * Storage keys for theme persistence per category
 */
export const THEME_STORAGE_KEYS: Record<ThemeCategory, string> = {
  student: 'lamma-student-theme',
  educator: 'lamma-educator-theme',
};

/**
 * Get a theme by its ID
 */
export function getTheme(themeId: string): Theme | undefined {
  return THEME_REGISTRY[themeId];
}

/**
 * Get all registered themes
 */
export function getAllThemes(): Theme[] {
  return Object.values(THEME_REGISTRY);
}

/**
 * Get themes filtered by category
 */
export function getThemesByCategory(category: ThemeCategory): Theme[] {
  return getAllThemes().filter((theme) => theme.category === category);
}

/**
 * Get themes filtered by mode (light/dark)
 */
export function getThemesByMode(mode: ThemeMode): Theme[] {
  return getAllThemes().filter((theme) => theme.mode === mode);
}

/**
 * Get the default theme for a category
 */
export function getDefaultTheme(category: ThemeCategory): Theme {
  const defaultId = DEFAULT_THEMES[category];
  const theme = THEME_REGISTRY[defaultId];

  if (!theme) {
    throw new Error(`Default theme "${defaultId}" not found for category "${category}"`);
  }

  return theme;
}

/**
 * Get the default theme ID for a category
 */
export function getDefaultThemeId(category: ThemeCategory): string {
  return DEFAULT_THEMES[category];
}

/**
 * Check if a theme ID exists in the registry
 */
export function isValidThemeId(themeId: string): boolean {
  return themeId in THEME_REGISTRY;
}

/**
 * Get theme IDs for a category
 */
export function getThemeIdsByCategory(category: ThemeCategory): string[] {
  return getThemesByCategory(category).map((theme) => theme.id);
}

/**
 * Theme metadata for UI display (without full theme data)
 */
export function getThemePreviews(category: ThemeCategory) {
  return getThemesByCategory(category).map((theme) => ({
    id: theme.id,
    name: theme.name,
    description: theme.description,
    mode: theme.mode,
    previewColors: theme.previewColors,
  }));
}

export default THEME_REGISTRY;
